<!--
  AbstractDropdown.svelte — Select-style dropdown built on flowbite-svelte Dropdown.

  Wraps Button + Dropdown + DropdownItem into a single component.
  The Popper inside Dropdown auto-triggers on the preceding Button;
  no manual onclick is needed.

  Usage (default):
    <AbstractDropdown {items} bind:value />

  Usage (custom rendering via children snippet):
    <AbstractDropdown {items} bind:value>
      {#snippet children(item)}
        <img src="/flags/{item.value}.svg" />
      {/snippet}
    </AbstractDropdown>
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import { Button, Dropdown, DropdownItem } from 'flowbite-svelte';
	import { t } from '../i18n';

	interface Item {
		name: string;
		value: string | number;
		icon?: string;
	}

	let {
		items,
		value = $bindable(),
		onchange,
		class: className = '',
		buttonClass = '',
		placeholder,
		size,
		ariaLabel,
		children
	}: {
		items: Item[];
		value?: string | number | null;
		onchange?: (_value: string | number) => void;
		class?: string;
		buttonClass?: string;
		placeholder?: string;
		size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
		/** Accessible name of the trigger, for a dropdown with no visible label (a filter). */
		ariaLabel?: string;
		children?: Snippet<[Item]>;
	} = $props();

	let isOpen = $state(false);
	let triggerWidth = $state(0);

	let selected = $derived(items.find((i) => String(i.value) === String(value)));
	let label = $derived(selected?.name ?? placeholder ?? $t('Select…'));
	let selectedIcon = $derived(selected?.icon);

	/** Handle item selection — update value, close dropdown, fire callback. */
	function select(item: Item) {
		value = item.value;
		isOpen = false;
		onchange?.(item.value);
	}
</script>

<div class="relative {className}" bind:clientWidth={triggerWidth}>
	<Button color="alternative" {size} class="w-full justify-between {buttonClass}" aria-label={ariaLabel}>
		{#if children && selected}
			{@render children(selected)}
		{:else}
			<span class="flex items-center gap-2 truncate">
				{#if selectedIcon}<i class="{selectedIcon} h-3 w-3 shrink-0"></i>{/if}
				{label}
			</span>
		{/if}
		<i class="fa-solid fa-chevron-down ms-2 h-3 w-3 shrink-0"></i>
	</Button>
	<Dropdown simple bind:isOpen triggerDelay={0} transitionParams={{ duration: 0 }} style="min-width: {triggerWidth}px">
		{#each items as item (item.value)}
			<DropdownItem onclick={() => select(item)}>
				{#if children}
					{@render children(item)}
				{:else}
					<span class="flex items-center gap-2">
						{#if item.icon}<i class="{item.icon} h-3 w-3 shrink-0"></i>{/if}
						{item.name}
					</span>
				{/if}
			</DropdownItem>
		{/each}
	</Dropdown>
</div>
